import type { SimEvent } from "../protocol/events";
import type { GridPos } from "../protocol/snapshot";
import type { RejectionCode } from "../protocol/driver";
import { assertNever } from "../util/assertNever";
import type { CardDef, PipeMaterial } from "./definitions";
import { NODE_DEFAULTS, PIPE_MAX_HP } from "./definitions";
import { totalUnits } from "./composition";
import { clearContamination } from "./hazards";
import { effectiveCostKw, heldPenalties } from "./hazardCards";
import { pollute } from "./pollution";
import { shuffle } from "./rng";
import { findNode, findNodeAt, isTileContaminated, type EngineEdge, type EngineNode, type EngineState } from "./state";

/**
 * Card resolution for every category in `02-cards-and-economy.md` `03-hazards.md` §2.
 *
 * Every play is validated before it mutates anything, so a rejected card
 * leaves the rig untouched and costs no power. `PlayResult.ok === false`
 * carries the reason, which the UI can surface.
 */

export type PlayResult =
  | { ok: true }
  | { ok: false; reason: string; code: RejectionCode; sourceId: string | null };

/**
 * Builds a refusal. `sourceId` names the node, edge or tile the refusal is
 * about (0012-driver-rejection-channel.worklog.md's T1 mapping table), or
 * `null` when the refusal is about the command itself.
 */
function refuse(
  reason: string,
  code: RejectionCode,
  sourceId: string | null = null,
): Extract<PlayResult, { ok: false }> {
  return { ok: false, reason, code, sourceId };
}

export interface PlayCardArgs {
  state: EngineState;
  card: CardDef;
  /** Which physical copy this play addresses (spec 0016) — resolved to a hand index before anything else runs. */
  instanceId: string;
  at: GridPos | null;
  /** Pipe cards need two endpoints; everything else ignores these. */
  fromNodeId?: string;
  toNodeId?: string;
  events: SimEvent[];
  /** Spec 0068: the command's recipe pick for a node-deploying card; omitted means the card's own binding. */
  recipeIds?: readonly string[];
  /**
   * The full catalogue (spec 0007). A directive surcharge is a property of
   * the OTHER cards in hand, so pricing one card requires knowing what every
   * other card in the hand is — `effectiveCostKw` (engine/hazardCards.ts)
   * needs this to resolve `card.hazard`/`isPlayable` for every held card id.
   */
  cards: Record<string, CardDef>;
}

export function playCard(args: PlayCardArgs): PlayResult {
  const { state, card, instanceId, at, events } = args;

  if (state.phase !== "play") return refuse("cards can only be played in the Play phase", "wrongPhase");
  const handIndex = state.hand.findIndex((ci) => ci.instanceId === instanceId);
  if (handIndex === -1) return refuse("card is not in hand", "notInHand");
  // `02-cards-and-economy.md` §2.2 Refused Plays Cost Zero: checked before
  // any mutation, including cost.
  if (card.category === "hazard" && card.isPlayable === false) {
    return refuse("this card cannot be played", "notPlayable");
  }

  // Spec 0068: a bad recipe pick is about the command, so it is refused before kW is weighed.
  let picked: readonly string[] | undefined;
  if (args.recipeIds !== undefined) {
    const checked = validateRecipeIds(state, card.deploysNode, args.recipeIds);
    if (!checked.ok) return checked;
    picked = checked.recipeIds;
  }

  const remaining = state.energyBudgetKw - state.energySpentKw;
  const cost = effectiveCostKw(state, args.cards, card);
  if (cost > remaining) return refuse("not enough power", "notEnoughPower");

  const result = applyCardEffect(picked === undefined ? args : { ...args, recipeIds: picked });
  if (!result.ok) return result;

  state.energySpentKw += cost;
  // Hardware becomes part of the rig; everything else is spent. Splicing by
  // the hand index resolved above (not a fresh lookup) means this always acts
  // on the exact instance the caller named, never "the first same-id copy".
  const [instance] = state.hand.splice(handIndex, 1);
  // A clearsOnPlay hazard card (Clogged Line, Chemical Hazard Spill) is
  // exiled — it returns to neither pile, per §4's "until played and cleared".
  if (!(card.category === "hazard" && card.clearsOnPlay)) {
    state.discardPile.push(instance!);
  }

  events.push({ type: "card.played", cardId: card.id, at: at ? { ...at } : null });
  return { ok: true };
}

/**
 * Spec 0068: the one validator for a recipe pick, shared by both construction
 * sites and `bindRecipe`. Returns the list in id order so two commands naming
 * the same set store identical state. `kind` is the node kind the recipes must
 * run on; `undefined` (a card that deploys no node) makes any pick invalid.
 */
export function validateRecipeIds(
  state: EngineState,
  kind: EngineNode["kind"] | undefined,
  recipeIds: readonly string[],
): { ok: true; recipeIds: string[] } | Extract<PlayResult, { ok: false }> {
  if (kind === undefined) return refuse("this card deploys no node, so it cannot be bound to recipes", "invalidRecipe");
  if (recipeIds.length === 0) return refuse("a recipe list cannot be empty", "invalidRecipe");
  const sorted = [...recipeIds].sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
  for (let i = 0; i < sorted.length; i++) {
    const id = sorted[i]!;
    if (i > 0 && sorted[i - 1] === id) return refuse(`recipe ${id} is named twice`, "invalidRecipe", id);
    const recipe = state.stage.recipes.find((r) => r.id === id);
    if (!recipe) return refuse(`unknown recipe: ${id}`, "invalidRecipe", id);
    if (!recipe.nodeKinds.includes(kind)) return refuse(`recipe ${id} does not run on a ${kind}`, "invalidRecipe", id);
  }
  return { ok: true, recipeIds: sorted };
}

function applyCardEffect(args: PlayCardArgs): PlayResult {
  const { card } = args;

  switch (card.category) {
    case "hardware":
      return applyHardware(args);
    case "attachment":
      return applyAttachment(args);
    case "directive":
      return applyDirective(args);
    // OQ2 (spec 0020): metaUtility is a recategorisation, not a new payload —
    // a metaUtility card still carries a `directive` effect, so it runs
    // through the same handler.
    case "metaUtility":
      return applyDirective(args);
    case "hazard":
      return applyHazard(args);
    default:
      return assertNever(card.category, "applyCardEffect");
  }
}

/**
 * `02-cards-and-economy.md` §2.2: `isPlayable === false` is refused before
 * `playCard` ever calls this
 * (Refused Plays Cost Zero), so every card reaching here is playable. Only
 * `clearsOnPlay` cards do anything — Clogged Line and Chemical Hazard Spill,
 * the only two hazard cards §4 names as playable at all.
 */
function applyHazard(args: PlayCardArgs): PlayResult {
  const { state, card, at, events } = args;
  if (!card.hazard) return refuse("hazard card has no effect", "noEffect");
  if (!card.clearsOnPlay) return { ok: true };

  switch (card.hazard.kind) {
    case "tileLock": {
      // The tile is read straight off the instance being played, not the
      // first matching entry in `heldPenalties` — that's what closing G-F7
      // means (spec 0016): the exact copy the caller named locks and clears
      // its own tile, never some other held copy's.
      const tile = state.cloggedLineLockedTiles[args.instanceId];
      if (!tile) return refuse("no tile to clear", "needsTarget");
      delete state.cloggedLineLockedTiles[args.instanceId]; // hygiene; the instance is about to be exiled from every pile anyway

      events.push({ type: "hazard.cardCleared", cardId: card.id, kind: "tileLock", at: { ...tile } });
      return { ok: true };
    }

    case "handSlotLock": {
      if (!at) return refuse("chemical hazard spill needs a target tile", "needsTarget");
      if (!isTileContaminated(state, at)) return refuse("tile is not contaminated", "targetContaminated", `${at.x},${at.y}`);

      clearContamination(state, at);
      events.push({ type: "hazard.cardCleared", cardId: card.id, kind: "handSlotLock", at: { ...at } });
      return { ok: true };
    }

    default:
      return refuse("hazard card has no effect", "noEffect");
  }
}

function applyHardware(args: PlayCardArgs): PlayResult {
  const { state, card, at, fromNodeId, toNodeId } = args;

  if (card.deploysPipe) {
    return deployPipe(args, card.deploysPipe, fromNodeId, toNodeId);
  }

  if (!card.deploysNode) return refuse("hardware card deploys nothing", "noEffect");
  if (!at) return refuse("hardware needs a target tile", "needsTarget");

  if (at.x < 0 || at.x >= state.stage.cols || at.y < 0 || at.y >= state.stage.rows) {
    return refuse("tile is off the board", "targetOffBoard", `${at.x},${at.y}`);
  }
  const occupying = findNodeAt(state, at);
  if (occupying) return refuse("tile is occupied", "targetOccupied", occupying.id);
  if (isTileContaminated(state, at)) return refuse("tile is contaminated", "targetContaminated", `${at.x},${at.y}`);
  if (heldPenalties(state, args.cards).lockedTiles.some((p) => p.x === at.x && p.y === at.y)) {
    return refuse("tile is locked", "targetLocked", `${at.x},${at.y}`);
  }

  state.nodes.push({
    id: `n${state.nextNodeSeq++}`,
    kind: card.deploysNode,
    pos: { ...at },
    rotation: NODE_DEFAULTS.rotation,
    buffer: [],
    bufferCapacity: card.deploysNodeConfig?.bufferCapacity ?? NODE_DEFAULTS.bufferCapacity,
    heatCounter: 0,
    criticalTemp: card.deploysNodeConfig?.criticalTemp ?? NODE_DEFAULTS.criticalTemp,
    isOverheatWarning: false,
    overheatTicks: 0,
    catalystHealth: 100,
    isDestroyed: false,
    guardCharges: 0,
    attachments: [],
    emits: null,
    // A hardware card never deploys an inputTank (CLAUDE.md schema impact).
    precursor: null,
    sourcingOptions: null,
    supplyRemaining: null,
    // A played thermalRegulator carries its card's configured ΔT, if any
    // (spec 0022) — same pattern as bufferCapacity/criticalTemp above. No
    // hardware card ever deploys an inputTank, so feedTemperature has
    // nothing to read here.
    deltaTemp: card.deploysNodeConfig?.deltaTemp ?? null,
    feedTemperature: null,
    splitCursor: 0,
    pumpOverride: card.deploysNodeConfig?.pumpOverride ?? 0,
    valveState: null,
    halfSpeedToggle: false,
    maxMultipleThisTurn: null,
    sludgeActiveThisTick: true,
    infestation: null,
    verminActiveThisTick: true,
    recipeIds: args.recipeIds ?? card.deploysNodeConfig?.recipeIds ?? null,
    // A playCard-driven placement is stage rig, not a Setup purchase — only
    // setup.ts's installHardware sets this non-null (spec 0017).
    installedHardware: null,
  });

  return { ok: true };
}

/** Pipes are edges, so placing one names two endpoints rather than a tile. */
function deployPipe(
  args: PlayCardArgs,
  material: PipeMaterial,
  fromNodeId: string | undefined,
  toNodeId: string | undefined,
): PlayResult {
  const { state } = args;
  const planned = planPipe(state, fromNodeId, toNodeId, heldPenalties(state, args.cards).lockedTiles);
  if (!planned.ok) return planned;
  layPipe(state, material, planned, null);
  return { ok: true };
}

export type PlannedPipe = { ok: true; fromNodeId: string; toNodeId: string; path: GridPos[] };

/**
 * Every check a new pipe must pass, and nothing else — no mutation, so a
 * caller with its own checks to run (Setup's budget, its instance mint) can
 * finish those before `layPipe` commits anything. Shared by Play's
 * `deployPipe` and Setup's `installHardware` (spec 0017) so the two routes
 * cannot disagree about where a pipe may go.
 */
export function planPipe(
  state: EngineState,
  fromNodeId: string | undefined,
  toNodeId: string | undefined,
  lockedTiles: readonly GridPos[],
): PlannedPipe | Extract<PlayResult, { ok: false }> {
  if (!fromNodeId || !toNodeId) return refuse("pipe needs two endpoints", "invalidPipeEndpoints");
  if (fromNodeId === toNodeId) return refuse("pipe cannot loop to itself", "invalidPipeEndpoints", fromNodeId);

  const from = findNode(state, fromNodeId);
  const to = findNode(state, toNodeId);
  if (!from || from.isDestroyed) return refuse("source node not found", "invalidPipeEndpoints", fromNodeId);
  if (!to || to.isDestroyed) return refuse("target node not found", "invalidPipeEndpoints", toNodeId);

  const existing = state.edges.find(
    (e) => e.fromNodeId === fromNodeId && e.toNodeId === toNodeId && !e.isBurst,
  );
  if (existing) return refuse("those nodes are already connected", "alreadyConnected", existing.id);

  const path = routeBetween(from.pos, to.pos);
  const contaminated = path.find((p) => isTileContaminated(state, p));
  if (contaminated) {
    return refuse("pipe would cross a contaminated tile", "targetContaminated", `${contaminated.x},${contaminated.y}`);
  }
  const locked = path.find((p) => lockedTiles.some((lt) => lt.x === p.x && lt.y === p.y));
  if (locked) {
    return refuse("tile is locked", "targetLocked", `${locked.x},${locked.y}`);
  }

  return { ok: true, fromNodeId, toNodeId, path };
}

/** Commits a pipe `planPipe` approved. Returns the new edge's id. */
export function layPipe(
  state: EngineState,
  material: PipeMaterial,
  planned: PlannedPipe,
  installedHardware: EngineEdge["installedHardware"],
): string {
  const id = `e${state.nextEdgeSeq++}`;
  state.edges.push({
    id,
    fromNodeId: planned.fromNodeId,
    toNodeId: planned.toNodeId,
    path: planned.path,
    material,
    hp: PIPE_MAX_HP,
    maxHp: PIPE_MAX_HP,
    pressureCounter: 0,
    isOverpressured: false,
    isLeaking: false,
    isBurst: false,
    transit: [],
    // No card in spec 0003 sets phaseRole on the pipe it places — that role
    // is authored on a stage's phase-converter output edges only. Same for
    // separationRole (spec 0023) and separator output edges.
    phaseRole: null,
    separationRole: null,
    installedHardware,
  });
  return id;
}

/**
 * Simple L-shaped route: horizontal first, then vertical. Deterministic.
 *
 * Exported because the UI draws a preview of the route while the player picks
 * the second endpoint — and a preview that disagrees with the pipe you get is
 * worse than no preview.
 */
export function routeBetween(from: GridPos, to: GridPos): GridPos[] {
  const path: GridPos[] = [{ ...from }];
  const stepX = Math.sign(to.x - from.x);
  const stepY = Math.sign(to.y - from.y);

  let x = from.x;
  let y = from.y;
  while (x !== to.x) {
    x += stepX;
    path.push({ x, y });
  }
  while (y !== to.y) {
    y += stepY;
    path.push({ x, y });
  }
  return path;
}

function applyAttachment(args: PlayCardArgs): PlayResult {
  const { state, card, at } = args;
  if (!card.attachment) return refuse("attachment card has no effect", "noEffect");
  if (!at) return refuse("attachment needs a target node", "needsTarget");

  const node = findNodeAt(state, at);
  if (!node) return refuse("no hardware on that tile", "needsTarget", `${at.x},${at.y}`);

  // Spec 0023: an attachment may be scoped to one NodeKind (today only the
  // Lid, to a wasteBuyer) — every attachment before it accepted any node.
  if (card.attachesToNodeKind && node.kind !== card.attachesToNodeKind) {
    return refuse(`a ${card.name} only fits a ${card.attachesToNodeKind}`, "wrongNodeKind", node.id);
  }

  const duplicate = node.attachments.some((a) => a.effect.kind === card.attachment!.kind);
  if (duplicate) return refuse("that attachment is already fitted", "alreadyConnected", node.id);

  node.attachments.push({ cardId: card.id, effect: card.attachment });

  // Attachments that simply raise a stat do it here and now, so every rule
  // reading that stat honours the upgrade without knowing attachments exist.
  switch (card.attachment.kind) {
    case "ptfeLining":
      // Upgrades every standard pipe leaving the node it protects.
      for (const edge of state.edges) {
        if (edge.fromNodeId === node.id && edge.material === "glass") edge.material = "ptfeLined";
      }
      break;

    case "insulation":
      node.criticalTemp += card.attachment.extraCriticalTemp;
      break;

    case "surgeDrum":
      node.bufferCapacity += card.attachment.extraBufferCapacity;
      break;

    case "guardBed":
      node.guardCharges += card.attachment.charges;
      break;

    // Cooling jackets and burst disks are read live, off the attachment list.
    // The Lid (spec 0023) is too — `dispatchScriptedEvents` (facility.ts)
    // checks for it live each turn rather than flipping a stat here.
    case "coolingJacket":
    case "burstDisk":
    case "lid":
      break;

    default:
      return assertNever(card.attachment, "applyAttachment");
  }

  return { ok: true };
}

function resolveDirectiveTarget(
  args: PlayCardArgs,
  kind: EngineNode["kind"],
): { ok: true; node: EngineNode } | Extract<PlayResult, { ok: false }> {
  const { state, card, at } = args;
  if (!at) return refuse(`${card.name} needs a target node`, "needsTarget");
  const node = findNodeAt(state, at);
  if (!node) return refuse("no hardware on that tile", "needsTarget", `${at.x},${at.y}`);
  if (node.kind !== kind) return refuse(`${card.name} only works on a ${kind}`, "wrongNodeKind", node.id);
  return { ok: true, node };
}

function applyDirective(args: PlayCardArgs): PlayResult {
  const { state, card, events } = args;
  if (!card.directive) return refuse("directive card has no effect", "noEffect");

  switch (card.directive.kind) {
    case "flashFreeze":
      // §2.1 mitigation: clears heat everywhere, resetting the explosion clock.
      for (const node of state.nodes) {
        if (node.isDestroyed) continue;
        node.heatCounter = 0;
        node.isOverheatWarning = false;
        node.overheatTicks = 0;
        events.push({
          type: "node.heated",
          nodeId: node.id,
          heatCounter: 0,
          criticalTemp: node.criticalTemp,
        });
      }
      return { ok: true };

    // Spec 0035: both aim at one node, checked like applyAttachment's — all
    // refusals land before any mutation, so a refused play spends no kW.
    case "stirVat": {
      const target = resolveDirectiveTarget(args, "incubator");
      if (!target.ok) return target;
      // residenceTicks only: holdTicks, the sludge guard and the half-speed
      // toggle are Solvent Flush's to undo, and a stir does not cure sludge.
      for (const packet of target.node.buffer) packet.residenceTicks = 0;
      return { ok: true };
    }

    case "lowFire": {
      const target = resolveDirectiveTarget(args, "reactor");
      if (!target.ok) return target;
      // A second play re-sets the same cap; it never stacks.
      target.node.maxMultipleThisTurn = card.directive.maxMultiple;
      return { ok: true };
    }

    case "solventFlush":
      // §2.5 mitigation: clears sludge and resets residence clocks.
      for (const node of state.nodes) {
        for (const packet of node.buffer) {
          packet.hasResidenceSludged = false;
          packet.residenceTicks = 0;
          packet.holdTicks = 0;
        }
        node.halfSpeedToggle = false;
      }
      return { ok: true };

    case "nitrogenPurge":
      // §2.2 mitigation: bleeds pressure off every intact line.
      for (const edge of state.edges) {
        if (edge.isBurst) continue;
        edge.pressureCounter = 0;
        edge.isOverpressured = false;
      }
      return { ok: true };

    case "emergencyFlush": {
      // §2.5's named mitigation. Blunter than a Solvent Flush: it dumps the
      // buffers rather than un-sludging them, so it costs you the material.
      let dumped = 0;
      for (const node of state.nodes) {
        if (node.isDestroyed || node.kind === "productHopper") continue;
        for (const packet of node.buffer) dumped += totalUnits(packet.composition);
        node.buffer = [];
        node.halfSpeedToggle = false;
      }
      state.wastedUnits += dumped;
      return { ok: true };
    }

    case "patchWeld": {
      // §2.3 maintenance: puts wall back on before a line opens up.
      const hp = card.directive.hp;
      for (const edge of state.edges) {
        if (edge.isBurst || edge.hp >= edge.maxHp) continue;
        edge.hp = Math.min(edge.maxHp, edge.hp + hp);
        if (edge.hp > 0) edge.isLeaking = false;
        events.push({ type: "pipe.corroded", edgeId: edge.id, hp: edge.hp });
      }
      return { ok: true };
    }

    case "catalystRecharge":
      // §2.4: fresh bed. Does not undo a poisoning that already cost yield.
      for (const node of state.nodes) {
        if (node.isDestroyed || node.kind !== "catalyticBed") continue;
        node.catalystHealth = 100;
        events.push({ type: "catalyst.poisoned", nodeId: node.id, catalystHealth: 100 });
      }
      return { ok: true };

    case "overtime":
      // Buys kW for THIS Play phase; the budget resets on the next one.
      state.energyBudgetKw += card.directive.extraKw;
      return { ok: true };

    case "rushOrder": {
      // Draws on top of the current hand rather than replacing it, so it is
      // an addition to the turn and not a mulligan.
      for (let i = 0; i < card.directive.cards; i++) {
        if (state.drawPile.length === 0) {
          if (state.discardPile.length === 0) break;
          state.drawPile = shuffle(state.discardPile, state.rng);
          state.discardPile = [];
        }
        const drawn = state.drawPile.shift();
        if (!drawn) break;
        state.hand.push(drawn);
      }
      return { ok: true };
    }

    case "fumigate": {
      // §3.3 (spec 0033, decision 5/12): clears the infestation on the node
      // at `at`. `at` null or no node on the tile refuses `needsTarget`; a
      // clean node refuses `noEffect` — both cost 0 kW (playCard only charges
      // after this returns ok).
      const { at } = args;
      if (!at) return refuse("fumigate needs a target tile", "needsTarget");
      const node = findNodeAt(state, at);
      if (!node) return refuse("no hardware on that tile", "needsTarget", `${at.x},${at.y}`);
      if (node.infestation === null) return refuse("this node is not infested", "noEffect", node.id);

      node.infestation = null;
      events.push({ type: "node.infestationCleared", nodeId: node.id, cause: "fumigated" });
      return { ok: true };
    }

    // Spec 0067 OQ11: the kW route to cleaning a tile. Refusals land before
    // playCard charges anything, so a scrub on a clean tile spends no kW and
    // the card stays in hand.
    case "scrubTile": {
      const { at } = args;
      if (!at) return refuse("scrub needs a target tile", "needsTarget");
      if (!isTileContaminated(state, at)) return refuse("tile is not contaminated", "noEffect", `${at.x},${at.y}`);

      clearContamination(state, at);
      events.push({ type: "tile.cleaned", pos: { ...at }, method: "directive", costCredits: 0 });
      return { ok: true };
    }

    default:
      return assertNever(card.directive, "applyDirective");
  }
}

/**
 * Hazard cards left in hand when the Play phase ends do their damage
 * (`02-cards-and-economy.md` §2.5: detrimental cards shuffled into the deck). Spec 0007's §4
 * table replaced the five bulk end-of-Play writes this used to make with nine
 * differentiated kinds; most of those are now read live elsewhere (see
 * `hazardCards.ts`'s doc comment and the Mechanism table) — this function
 * only still fires `drawPenalty` and `pipeStress`.
 */
export function applyUnplayedHazards(
  state: EngineState,
  cards: Record<string, CardDef>,
  events: SimEvent[],
): void {
  for (const instance of [...state.hand]) {
    const card = cards[instance.cardId];
    if (!card || card.category !== "hazard" || !card.hazard) continue;
    const cardId = instance.cardId;

    switch (card.hazard.kind) {
      case "drawPenalty":
        // Line Leak: additive across multiple copies, consumed by the very
        // next drawHand (state.ts).
        state.handSizePenaltyNextTurn += card.hazard.cards;
        events.push({
          type: "hazard.cardPenalty",
          cardId,
          kind: "drawPenalty",
          amount: card.hazard.cards,
          targetId: null,
        });
        break;

      case "pipeStress": {
        // Structural Shear: once per turn, deduped the same way
        // `pollutionSources` dedupes an incident — multiple held copies of
        // the same card id collapse into a single charge.
        const chargeKey = `${cardId}:${state.turn}`;
        if (state.chargedPenalties.includes(chargeKey)) break;
        state.chargedPenalties.push(chargeKey);

        for (const edge of state.edges) {
          if (edge.material !== "glass" || edge.isBurst) continue;
          const loss = Math.floor((edge.maxHp * card.hazard.percentOfMaxHp) / 100);
          edge.hp = Math.max(0, edge.hp - loss);
          events.push({ type: "pipe.corroded", edgeId: edge.id, hp: edge.hp });
          events.push({ type: "hazard.cardPenalty", cardId, kind: "pipeStress", amount: loss, targetId: edge.id });
          if (edge.hp === 0 && !edge.isLeaking) {
            edge.isLeaking = true;
            events.push({ type: "pipe.leaking", edgeId: edge.id });
            pollute(state, "leak", edge.id, events);
          }
        }
        break;
      }

      // Read live elsewhere, not at end of Play — see the Mechanism table:
      // powerPenalty/powerDrain at Play entry (phases.ts), directiveSurcharge
      // per play (hazardCards.ts's effectiveCostKw), maintenanceFee by
      // @reagent/meta at stage end, tileLock/handSlotLock as standing derived
      // state (hazardCards.ts/drawHand), infestation nowhere (spec 0033 owner
      // decision 8: a plain dead draw; `EngineNode.infestation` is the whole
      // mechanic).
      case "powerPenalty":
      case "directiveSurcharge":
      case "maintenanceFee":
      case "tileLock":
      case "handSlotLock":
      case "powerDrain":
      case "infestation":
        break;

      default:
        assertNever(card.hazard, "applyUnplayedHazards");
    }
  }
}
